/**
 * Settings tab: per-channel queue settings (threshold, ordering, chat command),
 * live chat ingestion, and (for streamers) mod management.
 *
 * The active channel comes from the switcher via the Outlet context. Mods may
 * change queue settings, but only the streamer (owner) can toggle chat
 * ingestion or manage mods — the role from the context gates those.
 *
 * Account connections live in the top-right Account menu, not here.
 */
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, type Settings as StreamSettings } from "../lib/api";
import type { ChannelContext } from "../components/Layout";
import { SettingsPanel } from "../components/SettingsPanel";
import { ModsPanel } from "../components/ModsPanel";
import { SettingsSkeleton } from "../components/Skeleton";

export function Settings() {
  const { channelId, role } = useOutletContext<ChannelContext>();
  const [settings, setSettings] = useState<StreamSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!channelId) return;
    setError(null);
    try {
      const res = await api.getSettings(channelId);
      setSettings(res.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [channelId]);

  // Drop the previous channel's settings on switch so the skeleton shows
  // instead of leaving stale settings on screen while the new ones load.
  // Kept separate from the `load` effect so re-running load() after a save
  // doesn't itself flash the skeleton.
  useEffect(() => {
    setSettings(null);
  }, [channelId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isStreamer = role === "streamer";

  // No channel (e.g. a mod with no grants) — the Layout banner explains.
  if (!channelId) return null;

  return (
    <div className="settings-stack">
      {error && <div className="error">Couldn’t load settings — {error}</div>}

      {settings ? (
        <SettingsPanel
          channelId={channelId}
          settings={settings}
          isStreamer={isStreamer}
          onSaved={load}
        />
      ) : (
        !error && <SettingsSkeleton />
      )}

      {isStreamer && <ModsPanel channelId={channelId} />}
    </div>
  );
}
